import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  Table,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  FormLabel,
  Select,
  Option,
  Input,
  IconButton,
} from '@mui/joy'
import {
  ArrowLeft as ArrowBackIcon,
  MagnifyingGlass as SearchIcon,
  Eye as VisibilityIcon,
  ArrowsClockwise as RefreshIcon,
} from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'

interface AnalysisListItem {
  id: number
  analysis_id: string
  job: number | null
  job_name: string | null
  photo: number
  photo_thumb_url: string
  status: string
  model_version: string
  created_at: string
  completed_at: string | null
  severity_score: number | null
  detection_count: number
  error_message: string | null
}

interface AnalysisListResponse {
  count: number
  next: string | null
  previous: string | null
  results: AnalysisListItem[]
}

export const AnalysisHistory: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '')
  const [jobFilter, setJobFilter] = useState('')
  const [severityFilter, setSeverityFilter] = useState('')

  // Build query params
  const queryParams = new URLSearchParams()
  if (statusFilter) queryParams.append('status', statusFilter)
  if (jobFilter) queryParams.append('job', jobFilter)
  if (severityFilter) queryParams.append('min_severity', severityFilter)
  queryParams.append('page', page.toString())

  // Fetch analyses
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['hawkeye-analyses', queryParams.toString()],
    queryFn: async () => {
      const response = await api.get<AnalysisListResponse>(
        `/api/hawkeye/analyses/?${queryParams.toString()}`
      )
      return response.data
    },
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success'
      case 'processing': return 'primary'
      case 'failed': return 'danger'
      case 'queued': return 'warning'
      default: return 'neutral'
    }
  }

  const getSeverityColor = (score: number | null) => {
    if (score === null) return 'neutral'
    if (score < 0.3) return 'success'
    if (score < 0.6) return 'warning'
    return 'danger'
  }

  const handleFilterChange = () => {
    setPage(1)
    const params = new URLSearchParams()
    if (statusFilter) params.append('status', statusFilter)
    setSearchParams(params)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="plain"
            startDecorator={<ArrowBackIcon />}
            onClick={() => navigate('/')}
          >
            Back
          </Button>
          <Typography level="h2">Analysis History</Typography>
        </Box>
        <IconButton onClick={() => refetch()}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Filters */}
      <CollapsibleFilters><Card sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', p: 2 }}>
          <FormControl size="sm" sx={{ minWidth: 150 }}>
            <FormLabel>Status</FormLabel>
            <Select
              value={statusFilter}
              onChange={(_, value) => {
                setStatusFilter(value as string)
                handleFilterChange()
              }}
              placeholder="All"
            >
              <Option value="">All</Option>
              <Option value="completed">Completed</Option>
              <Option value="processing">Processing</Option>
              <Option value="queued">Queued</Option>
              <Option value="failed">Failed</Option>
            </Select>
          </FormControl>

          <FormControl size="sm" sx={{ minWidth: 150 }}>
            <FormLabel>Min Severity</FormLabel>
            <Select
              value={severityFilter}
              onChange={(_, value) => {
                setSeverityFilter(value as string)
                handleFilterChange()
              }}
              placeholder="Any"
            >
              <Option value="">Any</Option>
              <Option value="0.3">Medium (30%+)</Option>
              <Option value="0.6">High (60%+)</Option>
              <Option value="0.8">Critical (80%+)</Option>
            </Select>
          </FormControl>

          <FormControl size="sm" sx={{ minWidth: 200 }}>
            <FormLabel>Job ID</FormLabel>
            <Input
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleFilterChange()
              }}
              placeholder="Filter by job ID"
              startDecorator={<SearchIcon />}
            />
          </FormControl>
        </Box>
      </Card></CollapsibleFilters>

      {error && (
        <Alert color="danger" sx={{ mb: 3 }}>
          Failed to load analyses. Please try again.
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <Card>
          <Table>
            <thead>
              <tr>
                <th style={{ width: 80 }}>Image</th>
                <th>Analysis ID</th>
                <th>Job</th>
                <th>Status</th>
                <th>Severity</th>
                <th>Detections</th>
                <th>Model</th>
                <th>Created</th>
                <th style={{ width: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data?.results?.map((analysis: any) => (
                <tr key={analysis.id}>
                  <td>
                    <img
                      src={analysis.photo_thumb_url}
                      alt="Thumbnail"
                      style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
                    />
                  </td>
                  <td>
                    <Typography level="body-sm" sx={{ fontFamily: 'monospace' }}>
                      {analysis.analysis_id.slice(0, 8)}...
                    </Typography>
                  </td>
                  <td>{analysis.job_name || 'N/A'}</td>
                  <td>
                    <Chip
                      size="sm"
                      color={getStatusColor(analysis.status)}
                      variant="soft"
                    >
                      {analysis.status}
                    </Chip>
                  </td>
                  <td>
                    {analysis.severity_score !== null ? (
                      <Chip
                        size="sm"
                        color={getSeverityColor(analysis.severity_score)}
                        variant="soft"
                      >
                        {(analysis.severity_score * 100).toFixed(0)}%
                      </Chip>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td>{analysis.detection_count}</td>
                  <td>
                    <Typography level="body-xs">{analysis.model_version}</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {new Date(analysis.created_at).toLocaleDateString()}
                    </Typography>
                  </td>
                  <td>
                    <IconButton
                      size="sm"
                      onClick={() => navigate(`/analysis/${analysis.id}`)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {data?.results?.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
                No analyses found
              </Typography>
            </Box>
          )}

          {/* Pagination */}
          {data && ((data as any).next || (data as any).previous) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, p: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                size="sm"
                disabled={!(data as any).previous}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Typography level="body-sm" sx={{ display: 'flex', alignItems: 'center' }}>
                Page {page}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                size="sm"
                disabled={!(data as any).next}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </Box>
          )}
        </Card>
      ) : null}
    </Box>
  )
}
